import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import qs from 'query-string';

import {
  constants,
  NotificationManager,
  PaymentsFormsApi,
  PaymentV2Api,
  SiteConfigManager,
  useApplicationState,
} from '@atlas/core';
import { DefaultPopup, Loader } from '@atlas/ui';

import { SIGN_IN_REDIRECT_URL_QUERY_KEY } from 'common/constants';
import history from 'components/Layout/history';
import PaymentsForm from 'src/common/Notifications/Popups/PaymentsFormPopup/PaymentsForm';
import PaymentMethodItem from 'src/components/ClientProfile/PaymentTab/PaymentMethodItem';

import { displayWithdrawalSuccessNotification } from '../withdrawalUtils';

const PaymentsFormPopup = ({
  data: {
    amount,
    formId,
    interpolationValues = {},
    paymentMethod,
    publicPaymentId,
    selectedPayment,
    type,
  },
  id,
}) => {
  const [formConfig, setFormConfig] = useState(null);
  const {
    authenticated,
    resolved,
    wallet: { currency },
  } = useApplicationState();
  const handleClose = useCallback(() => NotificationManager.removeNotification(id), [id]);

  useEffect(() => {
    PaymentsFormsApi.getPaymentsForms(formId)
      .then(formConfig => {
        setFormConfig(formConfig);
      })
      .catch(e => {
        handleClose();
        NotificationManager.display({
          data: {
            text: e.message,
            title: 'payments_form_error_title',
          },
          type: 'paymentFormError',
        });
      });
  }, [formId, handleClose]);

  const isDepositPage = type === constants.PAYMENT_OPERATION.DEPOSIT;
  const headerTitle = isDepositPage ? 'deposit' : 'withdrawal';
  const paymentMethodItem = isDepositPage ? paymentMethod : selectedPayment;

  const handleSubmit = useCallback(
    values => {
      const payload = {
        additionalParameters: {
          ...values,
          publicPaymentId,
        },
        amount,
        currency,
        pmId: isDepositPage ? paymentMethod?.id : selectedPayment?.id,
      };

      const request = isDepositPage
        ? PaymentV2Api.createAsyncDeposit
        : PaymentV2Api.createAsyncWithdrawalV2;

      request(payload).then(response => {
        handleClose();

        if (isDepositPage) return;

        displayWithdrawalSuccessNotification({
          amount,
          currency,
          extraData: { selectedPayment },
          paymentMethod,
          response,
        });
      });
    },
    [amount, currency, handleClose, isDepositPage, paymentMethod, publicPaymentId, selectedPayment],
  );

  useEffect(() => {
    if (resolved && !authenticated) {
      const redirectPath =
        SiteConfigManager.getExtraConfig('components.0.path') ||
        SiteConfigManager.getExtraConfig('components.0.defaultPath', '/');

      const updatedQueryString = qs.stringify({
        [SIGN_IN_REDIRECT_URL_QUERY_KEY]: history.location.pathname,
        cmd: 'signin',
      });

      const pathnameLogin = `${redirectPath}?${updatedQueryString}`;

      handleClose();
      history.push(pathnameLogin);
    }
  }, [resolved, authenticated, handleClose]);

  return (
    <DefaultPopup
      className="payments-form"
      headerTitle={headerTitle}
      headerTitlePosition="left"
      onClose={handleClose}
    >
      <div className="payments-form-wrapper">
        {formConfig ? (
          <>
            <PaymentMethodItem paymentMethod={paymentMethodItem} />
            <PaymentsForm
              formConfig={formConfig}
              interpolationValues={interpolationValues}
              onSubmit={handleSubmit}
            />
          </>
        ) : (
          <Loader />
        )}
      </div>
    </DefaultPopup>
  );
};

PaymentsFormPopup.propTypes = {
  data: PropTypes.shape({
    amount: PropTypes.number,
    formId: PropTypes.number,
    interpolationValues: PropTypes.object,
    paymentMethod: PropTypes.object,
    publicPaymentId: PropTypes.string.isRequired,
    selectedPayment: PropTypes.object,
    type: PropTypes.string,
  }),
  id: PropTypes.string.isRequired,
};

export default PaymentsFormPopup;
